//
// Hybrid adapter: reads papers from the real backend (`/projects/{id}/papers`),
// falls back to in-memory on error. Summary and group-corpus search still use
// in-memory (no backend endpoint yet).
// Import-job listing and creation route to the real backend.
// Swap this for a pure HttpProjectCorpus when the backend gains all read
// endpoints.
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Globals } from '../../global/globals';
import { derivePaperStatus } from '../domain/paper-status';
import { httpErrorToMessage } from './http-error-message';
import {
  CreateImportJobRequest,
  FailedUpload,
  GroupCorpusPaper,
  ImportJob,
  ImportSizeEstimate,
  ImportSourceType,
  Page,
  PageRequest,
  PaperItemStatus,
  PaperListFilter,
  PaperMetadataPatch,
  ProjectPaper,
  ProjectState,
  ProjectSummary,
} from '../domain/types';
import { ProjectCorpusPort } from '../ports/project-corpus.port';
import { ImportJobDto, mapImportJobDto } from '../domain/import-job-dto';
import { InMemoryProjectCorpus } from './in-memory-project-corpus';

/**
 * Backend DTO shape returned by POST /api/v1/projects/{id}/pdfs.
 */
interface UploadOutcomeDto {
  status: string;
  filename: string;
  document: Record<string, unknown> | null;
  errorCode: string | null;
  message: string | null;
}

interface BatchUploadResponse {
  jobId: number;
  results: UploadOutcomeDto[];
}

interface FailedUploadDto {
  id: number;
  filename: string;
  errorCode: string | null;
  message: string | null;
  createdAt: string;
}

/** Backend DTO for GET /api/v1/projects/{id}/papers. */
interface ProjectPaperDto {
  id: number;
  ptpId: number;
  title: string | null;
  authors: string | null; // comma-separated
  doi: string | null;
  abstractText: string | null;
  source: string | null; // Paper entity source field
  venue: string | null; // publication venue (journal/conference)
  hasPdf: boolean;
  duplicateOfPaperId: number | null;
  publicationDate: string | null; // ISO-8601
  addedAt: string; // ISO-8601
  sourceJobId: number | null;
  projectsLinkedCount: number;
  extractionStatus: string | null;
  pdfId: number | null;
  pdfUrl: string | null;
  secondStageIncluded?: boolean | null;
  workType: string | null;
  sourceType: string | null;
  externalId: string | null;
}

/** Backend DTO for GET /api/v1/projects/{id}/summary. */
interface ProjectSummaryDto {
  projectId: number;
  projectTitle: string;
  description: string | null;
  groupId: number;
  state: string;
  totalPapers: number;
  perSource: Record<string, number>;
  itemsNeedingAttention: number;
}

/** Backend DTO for GET /api/v1/groups/{gid}/papers. */
interface GroupCorpusPaperDto {
  id: number;
  title: string | null;
  authors: string | null;
  year: number | null;
  doi: string | null;
  alreadyInProject: boolean;
}

@Injectable()
export class HybridProjectCorpus implements ProjectCorpusPort {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);
  private readonly inMemory = inject(InMemoryProjectCorpus);
  // paperId → ProjectPaper cache (populated during toProjectPaper)
  private readonly paperCache = new Map<string, ProjectPaper>();
  // Last-known groupId resolved from project summary; defaults to 'current'
  private lastGroupId = 'current';

  // ────────────────────────────────────────────────────────────────────────
  //   Reads — in-memory fallback where backend lacks an endpoint
  // ────────────────────────────────────────────────────────────────────────

  getProjectSummary(projectId: string): Observable<ProjectSummary> {
    const url = `${this.globals.backendUri}/projects/${projectId}/summary`;
    return this.http.get<ProjectSummaryDto>(url).pipe(
      map((dto) => {
        this.lastGroupId = String(dto.groupId);
        return {
          projectId: String(dto.projectId),
          projectTitle: dto.projectTitle,
          description: dto.description,
          groupId: String(dto.groupId),
          state: dto.state as ProjectState,
          totalPapers: dto.totalPapers,
          perSource: dto.perSource as Record<ImportSourceType, number>,
          itemsNeedingAttention: dto.itemsNeedingAttention,
        };
      }),
      this.translateError(),
    );
  }

  listImportJobs(projectId: string, _page: PageRequest): Observable<Page<ImportJob>> {
    const url = `${this.globals.backendUri}/import-jobs?projectId=${projectId}`;
    return this.http.get<ImportJobDto[]>(url).pipe(
      map((dtos) => this.toImportJobPage(dtos, projectId)),
      this.translateError(),
    );
  }

  listPapers(
    projectId: string,
    filter: PaperListFilter,
    pageRequest: PageRequest,
  ): Observable<Page<ProjectPaper>> {
    const url = `${this.globals.backendUri}/projects/${projectId}/papers`;
    let params = new HttpParams()
      .set('page', String(pageRequest.page))
      .set('size', String(pageRequest.size));
    if (pageRequest.sort?.field) {
      params = params.set(
        'sort',
        pageRequest.sort.field +
          (pageRequest.sort.direction ? `,${pageRequest.sort.direction}` : ''),
      );
    }
    if (filter.q) params = params.set('q', filter.q);
    if (filter.source) params = params.set('source', filter.source);
    if (filter.status) params = params.set('status', filter.status);
    if (filter.importJobId) params = params.set('importJobId', filter.importJobId);
    if (filter.workType) params = params.set('workType', filter.workType);
    if (filter.sourceType) params = params.set('sourceType', filter.sourceType);
    return this.http.get<Page<ProjectPaperDto>>(url, { params }).pipe(
      map((page) => ({
        ...page,
        items: page.items.map((d) => this.toProjectPaper(d, projectId)),
      })),
      this.translateError(),
    );
  }

  searchGroupCorpus(
    groupId: string,
    q: string,
    projectId: string,
    page: PageRequest,
  ): Observable<Page<GroupCorpusPaper>> {
    const url = `${this.globals.backendUri}/groups/${groupId}/papers`;
    let params = new URLSearchParams({ projectId });
    if (q && q.trim()) params = new URLSearchParams({ projectId, q: q.trim() });
    return this.http.get<GroupCorpusPaperDto[]>(`${url}?${params}`).pipe(
      map((dtos) => {
        const mapped: GroupCorpusPaper[] = dtos.map((d) => ({
          id: String(d.id),
          title: d.title ?? '',
          authors: d.authors
            ? d.authors
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
          year: d.year,
          doi: d.doi,
          alreadyInProject: d.alreadyInProject,
        }));
        return this.paginate(mapped, page);
      }),
      catchError(() => this.inMemory.searchGroupCorpus(groupId, q, projectId, page)),
    );
  }

  previewOpenAlex(
    query: string,
    workTypes: string[],
    sourceTypes: string[],
    yearFrom?: number,
    yearTo?: number,
    language?: string[],
  ): Observable<ImportSizeEstimate> {
    const params = this.importFilterParams(
      query,
      workTypes,
      sourceTypes,
      yearFrom,
      yearTo,
      language,
    );
    const url = `${this.globals.backendUri}/import-jobs/estimateSize`;
    return this.http.get<ImportSizeEstimate>(url, { params }).pipe(this.translateError());
  }

  // ────────────────────────────────────────────────────────────────────────
  //   Writes — real backend where available, in-memory fallback for others
  // ────────────────────────────────────────────────────────────────────────

  createImportJob(projectId: string, request: CreateImportJobRequest): Observable<ImportJob> {
    switch (request.source) {
      case 'OPENALEX':
        return this.createOpenAlexImportJob(projectId, request);
      case 'BIBTEX':
        return this.createBibTexImportJob(projectId, request);
      case 'PDF':
        return this.uploadPdfs(projectId, request);
      case 'GROUP_CORPUS':
        return this.createGroupCorpusImportJob(projectId, request);
      default:
        // BIBTEX / GROUP_CORPUS — not wired to backend yet
        return this.inMemory.createImportJob(projectId, request);
    }
  }

  editPaperMetadata(paperId: string, patch: PaperMetadataPatch): Observable<ProjectPaper> {
    const cached = this.paperCache.get(paperId);
    if (!cached) {
      return this.inMemory.editPaperMetadata(paperId, patch);
    }
    const url = `${this.globals.backendUri}/projects/${cached.projectId}/papers/${paperId}`;
    return this.http.patch(url, this.toPaperUpdateBody(patch)).pipe(
      map(() => this.mergePatchIntoCache(paperId, patch)),
      this.translateError(),
    );
  }

  attachPdf(paperId: string, file: File): Observable<ProjectPaper> {
    const cached = this.paperCache.get(paperId);
    if (!cached) {
      return this.inMemory.attachPdf(paperId, file);
    }
    const projectId = cached.projectId;
    const url = `${this.globals.backendUri}/projects/${projectId}/papers/${paperId}/pdf`;
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<ProjectPaperDto>(url, form).pipe(
      map((dto) => this.toProjectPaper(dto, projectId)),
      this.translateError(),
    );
  }

  retryExtraction(pdfId: string, projectId: string): Observable<void> {
    const url = `${this.globals.backendUri}/groups/${this.lastGroupId}/pdfs/${pdfId}/extraction/retry`;
    const params = new URLSearchParams({ projectId });
    return this.http.post<void>(`${url}?${params}`, null).pipe(this.translateError());
  }

  advanceToScreening(projectId: string, force: boolean = false): Observable<void> {
    let url = `${this.globals.backendUri}/projects/${projectId}/advance-to-screening`;
    if (force) {
      url += '?force=true';
    }
    return this.http.post<void>(url, null).pipe(this.translateError());
  }

  listFailedUploads(jobId: string): Observable<FailedUpload[]> {
    const url = `${this.globals.backendUri}/import-jobs/${jobId}/failed-uploads`;
    return this.http.get<FailedUploadDto[]>(url).pipe(
      map((dtos) =>
        dtos.map((d) => ({
          id: String(d.id),
          filename: d.filename,
          errorCode: d.errorCode,
          message: d.message,
          createdAt: d.createdAt,
        })),
      ),
      this.translateError(),
    );
  }

  deleteFailedUpload(jobId: string, failureId: string): Observable<void> {
    const url = `${this.globals.backendUri}/import-jobs/${jobId}/failed-uploads/${failureId}`;
    return this.http.delete<void>(url).pipe(this.translateError());
  }

  updateImportJobTitle(jobId: string, title: string): Observable<ImportJob> {
    return this.http
      .patch<ImportJob>(`${this.globals.backendUri}/import-jobs/${jobId}/title`, { title })
      .pipe(this.translateError());
  }

  undoImportJob(jobId: string): Observable<void> {
    const url = `${this.globals.backendUri}/import-jobs/${jobId}`;
    return this.http.delete<void>(url).pipe(this.translateError());
  }

  // ────────────────────────────────────────────────────────────────────────
  //   Internals
  // ────────────────────────────────────────────────────────────────────────

  private createOpenAlexImportJob(
    projectId: string,
    request: CreateImportJobRequest & { source: 'OPENALEX' },
  ): Observable<ImportJob> {
    const params = this.importFilterParams(
      request.query,
      request.workTypes,
      request.sourceTypes,
      request.yearFrom,
      request.yearTo,
      request.language,
    ).set('projectId', projectId);
    const url = `${this.globals.backendUri}/import-jobs/create`;
    return this.http.post<ImportJobDto>(url, null, { params }).pipe(
      map((dto) => mapImportJobDto(dto, projectId)),
      this.translateError(),
    );
  }

  private createBibTexImportJob(
    projectId: string,
    request: CreateImportJobRequest & { source: 'BIBTEX' },
  ): Observable<ImportJob> {
    const form = new FormData();
    form.append('file', request.file, request.file.name);
    const params = new HttpParams().set('projectId', projectId);
    const url = `${this.globals.backendUri}/import-jobs/bibtex`;
    return this.http.post<ImportJobDto>(url, form, { params }).pipe(
      map((dto) => mapImportJobDto(dto, projectId)),
      this.translateError(),
    );
  }

  private uploadPdfs(
    projectId: string,
    request: CreateImportJobRequest & { source: 'PDF' },
  ): Observable<ImportJob> {
    const form = new FormData();
    for (const f of request.files) {
      form.append('files', f, f.name);
    }
    const url = `${this.globals.backendUri}/projects/${projectId}/pdfs`;
    return this.http.post<BatchUploadResponse>(url, form).pipe(
      map((response) => {
        const created = response.results.filter((r) => r.status === 'CREATED').length;
        const present = response.results.filter((r) => r.status === 'EXISTING').length;
        // Surface 207 Multi-Status ERROR outcomes instead of silently dropping them.
        const rejectedItems = response.results
          .filter((r) => r.status === 'ERROR')
          .map((r) => ({
            filename: r.filename,
            message: r.message ?? r.errorCode ?? 'The file could not be imported.',
          }));
        const descriptor =
          request.files.length === 1 ? request.files[0].name : `${request.files.length} PDFs`;
        const total = created + present;
        return {
          id: String(response.jobId),
          projectId,
          source: 'PDF',
          status: 'RUNNING',
          startedAt: new Date().toISOString(),
          finishedAt: null,
          actorDisplayName: 'You',
          startedByUserId: '',
          itemsAdded: created,
          papersMalformed: 0,
          papersSkipped: 0,
          itemsNeedingAttention: 0,
          descriptor,
          papersTotal: total,
          papersComplete: 0,
          workTypes: [],
          sourceTypes: [],
          title: null,
          failureReason: null,
          rejectedItems,
          papersIncompleteRemoved: 0,
          papersUndone: 0,
        } as ImportJob;
      }),
      this.translateError(),
    );
  }

  private createGroupCorpusImportJob(
    projectId: string,
    request: CreateImportJobRequest & { source: 'GROUP_CORPUS' },
  ): Observable<ImportJob> {
    const params = new URLSearchParams({ projectId });
    const url = `${this.globals.backendUri}/import-jobs/group-corpus?${params}`;
    const ids = request.paperIds.map((id) => Number(id));
    return this.http.post<ImportJobDto>(url, ids).pipe(
      map((dto) => mapImportJobDto(dto, projectId)),
      this.translateError(),
    );
  }

  /** Maps backend DTO list to a frontend Page<ImportJob>. */
  private toImportJobPage(dtos: ImportJobDto[], projectId: string): Page<ImportJob> {
    const items = dtos.map((dto) => mapImportJobDto(dto, projectId));
    return {
      items,
      totalItems: items.length,
      totalPages: 1,
      currentPage: 0,
      pageSize: items.length || 1,
    };
  }

  /**
   * Backend stores OpenAlex query as `"query:{query}"` in the source field.
   * Parse it back to a source type + descriptor. Other sources stored as-is.
   */
  private parseBackendSource(raw: string): { source: ImportSourceType; descriptor: string } {
    if (raw.startsWith('query:')) {
      return { source: 'OPENALEX', descriptor: raw.slice(6) };
    }
    const known: Record<string, ImportSourceType> = {
      PDF: 'PDF',
      BIBTEX: 'BIBTEX',
      GROUP_CORPUS: 'GROUP_CORPUS',
    };
    const src = known[raw];
    if (src) return { source: src, descriptor: this.describeSource(src) };
    return { source: 'OPENALEX', descriptor: raw };
  }

  private importFilterParams(
    query: string,
    workTypes: readonly string[],
    sourceTypes: readonly string[],
    yearFrom?: number,
    yearTo?: number,
    language?: string[],
  ): HttpParams {
    let params = new HttpParams().set('query', query);
    for (const workType of workTypes) {
      params = params.append('workTypes', workType);
    }
    for (const sourceType of sourceTypes) {
      params = params.append('sourceTypes', sourceType);
    }
    if (yearFrom != null) params = params.set('yearFrom', String(yearFrom));
    if (yearTo != null) params = params.set('yearTo', String(yearTo));
    if (language && language.length > 0) {
      for (const lang of language) {
        params = params.append('language', lang);
      }
    }
    return params;
  }

  private describeSource(source: ImportSourceType): string {
    switch (source) {
      case 'OPENALEX':
        return 'Search import';
      case 'PDF':
        return 'PDF upload';
      case 'BIBTEX':
        return 'BibTeX import';
      case 'GROUP_CORPUS':
        return 'Group corpus import';
    }
  }

  /** Maps PaperMetadataPatch → body for PATCH /projects/{id}/papers/{id}. */
  private toPaperUpdateBody(patch: PaperMetadataPatch): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    if (patch.title !== undefined) body.title = patch.title;
    if (patch.authors !== undefined) body.authors = patch.authors.join(', ');
    if (patch.doi !== undefined) body.doi = patch.doi;
    if (patch.abstractText !== undefined) body.abstractField = patch.abstractText;
    if (patch.venue !== undefined) body.venue = patch.venue;
    if (patch.year !== undefined) {
      body.publicationDate = patch.year != null ? new Date(patch.year, 0, 2).toISOString() : null;
    }
    if (patch.workType !== undefined) body.workType = patch.workType;
    if (patch.sourceType !== undefined) body.sourceType = patch.sourceType;
    return body;
  }

  /** Applies patch to cached paper and returns updated copy. */
  private mergePatchIntoCache(paperId: string, patch: PaperMetadataPatch): ProjectPaper {
    const cached = this.paperCache.get(paperId);
    if (!cached) {
      return {
        id: paperId,
        ptpId: 0,
        projectId: '',
        title: '',
        authors: [],
        year: null,
        venue: null,
        doi: null,
        abstractText: null,
        hasPdf: false,
        source: 'OPENALEX',
        status: 'COMPLETE',
        importJobId: '',
        addedAt: '',
        projectsLinkedCount: 0,
        extractionStatus: null,
        pdfId: null,
        pdfUrl: null,
        workType: null,
        sourceType: null,
        externalId: null,
      };
    }
    const updated: ProjectPaper = {
      ...cached,
      title: patch.title ?? cached.title,
      authors: patch.authors ?? cached.authors,
      year: patch.year !== undefined ? patch.year : cached.year,
      doi: patch.doi !== undefined ? patch.doi : cached.doi,
      abstractText: patch.abstractText !== undefined ? patch.abstractText : cached.abstractText,
      venue: patch.venue !== undefined ? patch.venue : cached.venue,
      workType: patch.workType !== undefined ? patch.workType : cached.workType,
      sourceType: patch.sourceType !== undefined ? patch.sourceType : cached.sourceType,
    };
    const recomputed: ProjectPaper = {
      ...updated,
      status:
        updated.status === 'DUPLICATE_SKIPPED'
          ? updated.status
          : derivePaperStatus({
              title: updated.title,
              abstractText: updated.abstractText,
              authors: updated.authors,
              isDuplicate: false,
            }),
    };
    this.paperCache.set(paperId, recomputed);
    return recomputed;
  }

  private translateError<T>(): MonoTypeOperatorFunction<T> {
    return catchError((err: unknown) => throwError(() => new Error(httpErrorToMessage(err))));
  }

  // ────────────────────────────────────────────────────────────────────────
  //   Paper DTO mapping
  // ────────────────────────────────────────────────────────────────────────

  private toProjectPaper(dto: ProjectPaperDto, projectId: string): ProjectPaper {
    const PAPER_SOURCE_MAP: Record<string, ImportSourceType> = {
      PDF_UPLOAD: 'PDF',
      OPEN_ALEX: 'OPENALEX',
      BIBTEX: 'BIBTEX',
    };
    const source = PAPER_SOURCE_MAP[dto.source ?? ''] ?? 'PDF';
    const status = this.deriveStatus(dto);
    const paper: ProjectPaper = {
      id: String(dto.id),
      ptpId: dto.ptpId,
      projectId,
      title: dto.title ?? '',
      authors: dto.authors
        ? dto.authors
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      year: dto.publicationDate ? new Date(dto.publicationDate).getFullYear() : null,
      venue: dto.venue ?? null,
      doi: dto.doi,
      abstractText: dto.abstractText,
      hasPdf: dto.hasPdf,
      source,
      status,
      importJobId: String(dto.sourceJobId ?? ''),
      addedAt: dto.addedAt,
      projectsLinkedCount: dto.projectsLinkedCount,
      extractionStatus:
        (dto.extractionStatus as import('../domain/types').ExtractionStatus) ?? null,
      pdfId: dto.pdfId != null ? String(dto.pdfId) : null,
      pdfUrl: dto.pdfUrl ?? null,
      secondStageIncluded: dto.secondStageIncluded,
      workType: dto.workType ?? null,
      sourceType: dto.sourceType ?? null,
      externalId: dto.externalId ?? null,
    };
    this.paperCache.set(paper.id, paper);
    return paper;
  }

  private deriveStatus(dto: ProjectPaperDto): PaperItemStatus {
    return derivePaperStatus({
      title: dto.title,
      abstractText: dto.abstractText,
      authors: dto.authors
        ? dto.authors
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      isDuplicate: dto.duplicateOfPaperId != null,
    });
  }

  private paginate<T>(items: readonly T[], page: PageRequest): Page<T> {
    const size = Math.max(1, page.size);
    const start = Math.max(0, page.page) * size;
    const totalElements = items.length;
    return {
      items: items.slice(start, start + size),
      totalItems: totalElements,
      totalPages: Math.max(1, Math.ceil(totalElements / size)),
      currentPage: page.page,
      pageSize: size,
    };
  }
}
